import { createSlice, PayloadAction, Draft } from "@reduxjs/toolkit";
import { InvoiceResponseDto } from "../../shared/dtos/invoice/InvoiceResponseDto";
import { InvoiceSummaryDto } from "../../shared/dtos/invoice/InvoiceSummaryDto";
import { SaleType } from "../../shared/enums/SaleType";
import {
  getInitialCrudState,
  generateCrudReducers,
} from "../utils/crudFactory";

export interface InvoiceCartItem {
  itemId: string;
  name: string;
  qty: number;
  price: number;
  discount: number;
  brand?: string;
  category?: string;
}

interface InvoiceInitialState {
  cartItems: InvoiceCartItem[];
  selectedCustomer: Record<string, unknown> | null;
  saleType: SaleType;
  cartSubtotal: number;
}

const calculateSubtotal = (items: InvoiceCartItem[]): number =>
  items.reduce(
    (acc, item) =>
      acc +
      (Number(item.price) - Number(item.discount || 0)) * Number(item.qty),
    0,
  );

const loadInitialState = (): InvoiceInitialState => {
  try {
    const items = JSON.parse(localStorage.getItem("inv_items") || "[]");
    const customer = JSON.parse(localStorage.getItem("inv_customer") || "null");
    const saleType =
      (localStorage.getItem("inv_saleType") as SaleType) || SaleType.WHOLESALE;
    return {
      cartItems: items as InvoiceCartItem[],
      selectedCustomer: customer as Record<string, unknown> | null,
      saleType,
      cartSubtotal: calculateSubtotal(items),
    };
  } catch (e) {
    return {
      cartItems: [],
      selectedCustomer: null,
      saleType: SaleType.WHOLESALE,
      cartSubtotal: 0,
    };
  }
};

const initialState = {
  ...getInitialCrudState<InvoiceResponseDto, InvoiceSummaryDto>(),
  ...loadInitialState(),

  loadingPayment: false,
  loadingVoid: false,
  loadingPdf: false,
  loadingEmail: false,

  errorPayment: null as string | null,
  errorVoid: null as string | null,
  errorPdf: null as string | null,
  errorEmail: null as string | null,

  pdfUrl: null as string | null,
};

export const invoiceSlice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    ...generateCrudReducers<InvoiceResponseDto, InvoiceSummaryDto>(),

    setLoadingPayment(state) {
      state.loadingPayment = true;
      state.errorPayment = null;
    },
    setLoadingVoid(state) {
      state.loadingVoid = true;
      state.errorVoid = null;
    },
    setLoadingPdf(state) {
      state.loadingPdf = true;
      state.errorPdf = null;
    },
    setLoadingEmail(state) {
      state.loadingEmail = true;
      state.errorEmail = null;
    },

    setErrorPayment(state, action: PayloadAction<string | null>) {
      state.loadingPayment = false;
      state.errorPayment = action.payload;
    },
    setErrorVoid(state, action: PayloadAction<string | null>) {
      state.loadingVoid = false;
      state.errorVoid = action.payload;
    },
    setErrorPdf(state, action: PayloadAction<string | null>) {
      state.loadingPdf = false;
      state.errorPdf = action.payload;
    },
    setErrorEmail(state, action: PayloadAction<string | null>) {
      state.loadingEmail = false;
      state.errorEmail = action.payload;
    },

    setPdfUrl(state, action: PayloadAction<string>) {
      state.loadingPdf = false;
      state.pdfUrl = action.payload;
    },
    clearPdfUrl(state) {
      if (state.pdfUrl) URL.revokeObjectURL(state.pdfUrl);
      state.pdfUrl = null;
    },
    setEmailSuccess(state) {
      state.loadingEmail = false;
    },
    paymentSuccess(state, action: PayloadAction<InvoiceResponseDto>) {
      state.loadingPayment = false;
      state.item = action.payload;
    },

    // --- Cart Reducers ---
    setInvSaleType(state, action: PayloadAction<SaleType>) {
      state.saleType = action.payload;
      localStorage.setItem("inv_saleType", action.payload);
    },
    setInvCustomer(
      state,
      action: PayloadAction<Record<string, unknown> | null>,
    ) {
      state.selectedCustomer = action.payload as Draft<
        Record<string, unknown>
      > | null;
      localStorage.setItem("inv_customer", JSON.stringify(action.payload));
    },
    invItemAdd(state, action: PayloadAction<InvoiceCartItem>) {
      const existing = state.cartItems.find(
        (i) => i.itemId === action.payload.itemId,
      );
      if (existing) {
        existing.qty += action.payload.qty;
      } else {
        state.cartItems.push(action.payload as Draft<InvoiceCartItem>);
      }
      state.cartSubtotal = calculateSubtotal(
        state.cartItems as InvoiceCartItem[],
      );
      localStorage.setItem("inv_items", JSON.stringify(state.cartItems));
    },
    invItemUpdateFields(
      state,
      action: PayloadAction<{
        itemId: string;
        updates: Partial<InvoiceCartItem>;
      }>,
    ) {
      const item = state.cartItems.find(
        (i) => i.itemId === action.payload.itemId,
      );
      if (item) {
        if (action.payload.updates.qty !== undefined)
          item.qty = Number(action.payload.updates.qty);
        if (action.payload.updates.price !== undefined)
          item.price = Number(action.payload.updates.price);
        if (action.payload.updates.discount !== undefined)
          item.discount = Number(action.payload.updates.discount);
        if (item.qty <= 0)
          state.cartItems = state.cartItems.filter(
            (i) => i.itemId !== action.payload.itemId,
          );
      }
      state.cartSubtotal = calculateSubtotal(
        state.cartItems as InvoiceCartItem[],
      );
      localStorage.setItem("inv_items", JSON.stringify(state.cartItems));
    },
    invItemRemove(state, action: PayloadAction<string>) {
      state.cartItems = state.cartItems.filter(
        (i) => i.itemId !== action.payload,
      );
      state.cartSubtotal = calculateSubtotal(
        state.cartItems as InvoiceCartItem[],
      );
      localStorage.setItem("inv_items", JSON.stringify(state.cartItems));
    },
    clearInvCart(state) {
      state.cartItems = [];
      state.cartSubtotal = 0;
      state.selectedCustomer = null;
      localStorage.removeItem("inv_items");
      localStorage.removeItem("inv_customer");
    },
  },
});

export const invoiceActions = invoiceSlice.actions;
export default invoiceSlice.reducer;
